<template>
  <div class="c-color-filter">
    <template v-if="showColors">
      <div
        v-for="(group, name, index) in colorTypes"
        :key="`color-types-group-${name}-${index}`"
        class="c-color-filter__color-groups u-margin-bottom-x-small"
      >
        <template v-if="name == property.label || (name == 'hue' && property.label == 'Color')">
          <div 
            v-if="name == 'hue' && property.label == 'Color'"
            class="c-color-filter__color-options l-grid"
          >
            <!--
              group by type
              only display swatches for hues
            -->
            <button
              v-for="colorType in group"
              :key="`${colorType.value}-${index}`"
              class="c-color-filter__color-type"
              :class="{
                'c-color-filter__color-type--active': isEnabled(colorType.value),
                'c-color-filter__color-type--hue': name === 'hue',
              }"
              role="checkbox"
              :aria-checked="isEnabled(colorType.value)"
              @click="toggleFilter(colorType.value)"
            >
              <swatch
                v-if="name === 'hue'"
                :color="colorType"
                :active="isEnabled(colorType.value)"
                :show-image="false"
              />
              <span
                class="c-color-filter__color-type-label"
                :class="{
                  'c-color-filter__color-type-label--hue': name === 'hue',
                }"
              >
                {{ colorType.name }}
              </span>
            </button>
          </div>
          <div
            v-else-if="property.label != 'Color'"
            class="c-color-filter__finish-type"
            :class="{
              'l-grid l-grid--3-col l-flex--justify-start': direction == 'row',
              'l-flex l-flex--column l-flex--align-start': direction == 'column',
            }"
          >
            <label
              v-for="colorType in group"
              :key="colorType.value"
              class="c-color-filter__label u-margin-bottom-x-small h-desktop"
            >
              {{ colorType.name }}
              <Checkbox
                :name-for="colorType.name"
                :value="isEnabled(colorType.value)"
                @input="toggleFilter(colorType.value)"
              />
            </label>
            <Checkbox
              v-for="colorType in group"
              :key="`${colorType.value}-${index}`"
              :name-for="colorType.name"
              class="u-margin-bottom-x-small h-mobile"
              :value="isEnabled(colorType.value)"
              @input="toggleFilter(colorType.value)"
            >
              {{ colorType.name }}
            </Checkbox>
          </div>
        </template>
      </div>
    </template>
    <div
      v-else
      class="c-color-filter__color-options l-flex l-flex--justify-start u-margin-bottom-small"
    >
      <swatch
        v-for="(color, index) in colors"
        :key="`${index}-${color.name}`"
        :color="color"
        :active="isEnabled(color.name)"
        :show-image="true"
        class="c-color-filter__swatch"
        @click="toggleFilter(color.name)"
      />
    </div>
  </div>
</template>

<script>
import capitalize from 'lodash/capitalize'
import FilterMixin from '@/mixins/filter'
import flow from 'lodash/fp/flow'
import get from 'lodash/get'
import isArray from 'lodash/isArray'
import isString from 'lodash/isString'
import kebabCase from 'lodash/fp/kebabCase'
import Swatch from '../swatch/Swatch'
import toLower from 'lodash/fp/toLower'
import trim from 'lodash/trim'
import Checkbox from '../checkbox/Checkbox.vue'

const toSlug = flow((s) => trim(s), kebabCase, toLower)
const toLabel = flow((s) => trim(s), capitalize)

// const colorSwatchLimit = process.env.COLOR_FILTER_SWATCH_LIMIT || 16
const colorGroups = process.env.COLOR_FILTER_GROUPS || [
  'hue',
  'finish',
  'tone',
]
const colorMap = {
  'navy-blue': 'rgb(50, 56, 72)',
  white: '#fff',
  black: '#000',
  grey: '#eee',
  green: 'rgb(114,113,79)'
}

export default {
  components: {
    Swatch,
    Checkbox
  },
  filters: { capitalize },
  mixins: [
    FilterMixin
  ],
  props: {
    direction: {
      type: String,
      default: 'column'
    }
  },
  computed: {
    showColors() {
      const colors = this.colors
      return colors && colors.length
    },
    colorTypes() {
      const colors = this.colors
      let result = {}
      for (let color of colors) {
        for (let group of colorGroups) {
          if (!result[group]) {
            result[group] = {}
          }
          this.reduceColorType(group, color, result[group])
        }
      }
      return result
    },
    colors() {
      let result = {}
      for (let color of this.values.filter(e => typeof e == 'object' && !e.swatchImage.includes('collection'))) {
        result[toSlug(color.name)] = color
      }
      return Object.values(result)
    }
  },
  methods: {
    /**
     * ignore inked (printed), etched, dual-tone
     */
    reduceColorType(type, color, result) {
      // TODO: this section needs to filter the correct for each group
      if (color[type]) {
        // make sure we end up with a consistent type for the for..of loop
        let colorTypeValues = []
        if (isArray(color[type])) {
          colorTypeValues.push(...color[type])
        } else if (isString(color[type])) {
          colorTypeValues.push(...color[type].split(','))
        }
        for (let value of colorTypeValues) {
          const slug = toSlug(value)
          result[slug] = {
            name: toLabel(value),
            value: `${type}:${trim(value)}`,
            swatchColor: colorMap[slug]
          }
          if (
            !/dual\s?tone/i.test(color.tone) &&
            !/printed/i.test(color.finish) &&
            !colorMap[slug]
          ) {
            result[slug].swatchColor = (
              color.swatchColor || get(result, `${slug}.swatchColor`)
            )
            result[slug].swatchImage = (
              color.swatchImage || get(result, `${slug}.swatchImage`)
            )
          }
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.c-color-filter {
  width: 100%;

  &__swatch {
    margin-right: rem(12px);
    margin-bottom: rem(12px);
  }
  &__color {
    position: relative;
    width: rem(52px);
    height: rem(52px);
    border-radius: 100%;
    margin: rem(10px);
    background-position: 50%;
    background-size: contain;
    background-clip: padding-box;
    @include themify($themes) {
      border: 1px solid themed('background', 'dark');
    }
    &:hover {
      cursor: pointer;
    }
    &--active {
      border: 1px solid red;
      &::after {
        position: absolute;
        $after-border-size: 135%;
        top:  ($after-border-size - 100%) / -2;
        left: ($after-border-size - 100%) / -2;
        content: '';
        display: block;
        width:  $after-border-size;
        height: $after-border-size;
        border-radius: 100%;
        @include themify($themes) {
          border: 1px solid themed('foreground', 'dark');
        }
      }
    }
  }
  &__color-type {
    margin-bottom: rem(30px);
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    width: auto;
    height: rem(52px);
    background-color: transparent;
    border: none;
    &:hover {
      cursor: pointer;
    }
    &--hue {
      justify-content: flex-start;
    }
    &--active {
      .c-swatch__color-image {
        padding: 1px;
      }
      .c-swatch-wrapper {
        border: 1px solid black;
        border-radius: 50%;
    }
    }
  }
  &__color-type-label {
    &--hue {
      margin-top: rem(7px);
    }
  }
  &__color-options {
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
    grid-auto-rows: minmax(rem(40px), auto);
    @include respond(phone) {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  &__finish-type {
    column-gap: 1.25rem;
  }

  .l-grid {
    grid-gap: rem(10px);
  }

  &__label {
    font-size: 1.6rem;
    font-weight: 300;
    letter-spacing: 0.05rem;
    line-height: 2.1rem;
  }
}

.h-desktop {
  display: none;

  @include respond(phone) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }
}

.h-mobile {
  display: block;

  @include respond(phone) {
    display: none;
  }
}
</style>
